/**
 * Interactive tutorials — the only state that survives a page load.
 *
 * The tour walks the florist from the Events index (ERB, entrypoints/application)
 * into the event editor (Vue, entrypoints/vue). That hop is ALWAYS a hard,
 * full-document reload — events#show sets <meta name="turbo-visit-control"
 * content="reload"> and the editor bundle never imports Turbo — so there is no
 * in-memory state to hand over. sessionStorage is not a convenience here, it is
 * the only channel (docs/INTERACTIVE_TUTORIALS_PLAN.md §1.2).
 *
 * sessionStorage, not localStorage (which the React prototype used): a tour is a
 * single sitting, and a half-finished run should not be waiting in a tab opened
 * next week.
 *
 * ONE key holding {tourId, phase, cpIndex}, rather than the handoff README's
 * per-tour `bz_tut_first_quote_cp`. Only one tour can be running at a time, so a
 * key per tour would need a sweep across every registry id on boot to find which
 * one (if any) is live; the tour id belongs in the value. A second tutorial
 * costs no new key.
 */

/** Which card the engine is showing. `null` (no stored state) = nothing running. */
export type TourPhase = 'welcome' | 'run' | 'done'

export interface TourState {
  /** `id` of the entry in registry.ts's TOURS. */
  tourId: string
  phase: TourPhase
  /** Index into that tour's `checkpoints`. Meaningless unless phase is 'run'. */
  cpIndex: number
}

export const TOUR_STATE_KEY = 'bz_tut_cp'

/**
 * A second, independent key: "the florist is still owed the 'tutorial ended'
 * toast".
 *
 * Deliberately not a `phase` on the state above. The run is over — the value
 * under TOUR_STATE_KEY has been removed — and reviving it to carry a message
 * would mean every reader of a run had to know about a run that isn't one. The
 * two keys are also written in the same breath by the same caller, so keeping
 * clearTourState() away from this one is what stops the clear from eating the
 * marker it was just given.
 *
 * The flag holds no copy, only the fact. A message stored here would be written
 * by one deploy and rendered by the next.
 */
export const TOUR_EXIT_TOAST_KEY = 'bz_tut_exit_toast'

const PHASES: TourPhase[] = ['welcome', 'run', 'done']

/**
 * Storage can throw rather than return null — Safari's private mode raises on
 * write, and an embedded/blocked context raises on mere access. A tutorial must
 * never be the reason a page errors, so every touch is guarded and a failure
 * degrades to "no state" (the tour simply doesn't resume).
 */
function storage(): Storage | null {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

/**
 * The stored state, or null if there is none, it is unreadable, or it is not
 * shaped like a TourState.
 *
 * The shape check is not defensive decoration: this value is written by an
 * older deploy's engine as often as by this one — the florist reloads mid-tour
 * onto new code — and a bad cpIndex would index past the end of the checkpoint
 * script. Validating here means the caller never has to.
 */
export function readTourState(): TourState | null {
  const store = storage()
  if (!store) return null

  let raw: string | null
  try {
    raw = store.getItem(TOUR_STATE_KEY)
  } catch {
    return null
  }
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null

    const { tourId, phase, cpIndex } = parsed as Record<string, unknown>
    if (typeof tourId !== 'string' || !tourId) return null
    if (typeof phase !== 'string' || !PHASES.includes(phase as TourPhase)) return null
    if (typeof cpIndex !== 'number' || !Number.isInteger(cpIndex) || cpIndex < 0) return null

    return { tourId, phase: phase as TourPhase, cpIndex }
  } catch {
    return null
  }
}

/** Persist the running state. Silently a no-op if storage is unavailable. */
export function writeTourState(state: TourState): void {
  const store = storage()
  if (!store) return
  try {
    store.setItem(TOUR_STATE_KEY, JSON.stringify(state))
  } catch {
    /* full or blocked storage: the tour still runs, it just won't survive a reload */
  }
}

/** Forget the run. Called on exit and on completion — never on a page unload. */
export function clearTourState(): void {
  const store = storage()
  if (!store) return
  try {
    store.removeItem(TOUR_STATE_KEY)
  } catch {
    /* nothing to do — see writeTourState */
  }
}

/**
 * Remember that the exit toast has not been seen yet.
 *
 * Ending a run by clicking a header link renders the toast onto a document the
 * browser is in the middle of replacing, so nobody ever reads it — the florist
 * gets no answer to "why did the tutorial vanish?". This carries it to the page
 * they are actually going to look at.
 */
export function markExitToastPending(): void {
  const store = storage()
  if (!store) return
  try {
    store.setItem(TOUR_EXIT_TOAST_KEY, '1')
  } catch {
    /* see writeTourState — the toast is lost, nothing else is */
  }
}

/**
 * True at most once per marker: reading it also clears it.
 *
 * Read-and-clear rather than read-then-let-the-caller-clear, because the
 * failure mode of a forgotten clear is a toast that reappears on every page
 * load for the rest of the session, which is worse than the toast nobody saw.
 */
export function takePendingExitToast(): boolean {
  const store = storage()
  if (!store) return false
  try {
    const pending = store.getItem(TOUR_EXIT_TOAST_KEY) === '1'
    if (pending) store.removeItem(TOUR_EXIT_TOAST_KEY)
    return pending
  } catch {
    return false
  }
}
